import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Link,
  Skeleton,
  Stack,
  styled,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect } from "react";
import AssetThumbnail from "../../Misc/AssetThumbnail";
import AssetStatus from "../../Misc/Status/AssetStatus/asset-status";

import AutorenewIcon from "@mui/icons-material/Autorenew";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router";
import { assetService, fileService, ingestService, mediaService, workflowService } from "../../../Core/Api/services";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { ActivityKind } from "../../../Core/Api/Types/activity-kind";
import { AssetPanelDto } from "../../../Core/Api/Types/db-asset-object-infos";
import { AssetMediaCardDto } from "../../../Core/Api/Types/db-file-infos";
import { ObjectClass } from "../../../Core/Api/Types/object-class";
import { ObjectStatus } from "../../../Core/Api/Types/object-status";
import { ObjectType } from "../../../Core/Api/Types/object-type";
import GlobalConst from "../../../Core/Utils/Constants/globals";
import { routePaths } from "../../../Router/router";
import { AssetTypeAlphaColor, GetFileSize, UserHasAcl, UserHasAcls } from "../../../Utils/helpers";
import useCustomSnackbar from "../../../Utils/Hooks/useCustomSnackbar";
import MediaCard from "../../Cards/MediaCard/media-card";
import AssetTypeChip from "../../Misc/Chips/AssetChip/asset-type-chip";
import MetadataListItem from "../../Misc/Metadata/MetadataListItem";
import MetadataListTextItem from "../../Misc/Metadata/MetadataListTextItem/metadata-list-text-item";
import { ModalContext } from "../../Modals";
import ModalConfirmActionOpenButton from "../../Modals/ModalConfirmAction/modal-confirm-action-open-button";
import ModalEditMetadata, { ModalEditMetadataElementProps } from "../../Modals/ModalEditMetadata/modal-edit-metadata";
import ModalOrderFileFixingOpenButton from "../../Modals/ModalOrderFileFixing/modal-order-file-fixing-open-button";
import ModalRequestPurgeOpenButton from "../../Modals/ModalPurgeAsset/modal-request-purge-open-button";
import ResourceNotification from "../../Notification/resource-notification";
import Panel, { PanelProps } from "../Panel/panel";
import PanelAccordion from "../PanelAccordion/panel-accordion";
import PanelHeader, { PanelHeaderProps } from "../PanelHeader/panel-header";
import PanelTabContent from "../PanelTabContent/panel-tab-content";
import PanelTabs from "../PanelTabs";
import { PreviewContext } from "../../../Pages/admin/Inventory/InventoryAssetSingle/AssetSinglePreview/asset-single-preview";

export interface AssetPanelProps extends PanelProps {
  assetId?: number;
  rowId?: number;
  closePanel: () => void;
}

const AssetPanel: React.FC<AssetPanelProps> = (props: AssetPanelProps) => {
  // TODO : when we can request download link, title text is not sticked to top => stick it to top => later

  // context
  const { t } = useTranslation();
  const modalContext = useContext(ModalContext);
  const { openModal } = { ...modalContext };

  // props
  const { open, onClose, closePanel, assetId, rowId, ...otherProps } = props;

  // snackbar
  const enqueueCustomSnackbar = useCustomSnackbar();

  // states
  const [tabValue, setTabValue] = React.useState(0);
  const [asset, setAsset] = React.useState<AssetPanelDto>();

  // functions
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleEditMetadata = () => {
    if (!openModal || !assetId) return;

    assetService.GetObjectById(assetId).then((res) => {
      if (res.success && res.graph) {
        openModal<ModalEditMetadataElementProps>(ModalEditMetadata, { objects: [res.graph] });
      } else enqueueCustomSnackbar(res.message || t("alert:error.assetNotFound"), "error");
    });
  };

  const handleDeleteAsset = (comment?: string) => {
    ingestService.DeleteRequest({ id: asset!.id, comment: comment || "" }).then((response) => {
      if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
        enqueueCustomSnackbar(response.message!, "primary");
      else enqueueCustomSnackbar(t("alert:success.assetDeleted", { id: asset!.id }), "success");
      closePanel();
    });
  };

  const handleBypassRejection = (comment?: string) => {
    workflowService.AssetByPassRejection(asset!.id, comment).then((response) => {
      if (response.rescode === GlobalConst.ELEVATION_RESPONSE_OBJECT_ID)
        enqueueCustomSnackbar(response.message!, "primary");
      else enqueueCustomSnackbar(t("alert:success.assetRejectionBypassed", { id: asset!.id }), "success");
    });
  };

  // useEffect
  React.useEffect(() => {
    if (assetId && assetId > 0) {
      Promise.all([
        assetService.GetAssetForAssetPanel(assetId),
        mediaService.GetMediaFilesOfAssetForAssetPanel(assetId),
      ]).then(([asset, assetMedias]) => {
        if (asset !== undefined) {
          asset.assetMedia = assetMedias;
          setAsset(asset);
        }
      });
    }
  }, [assetId]);

  return (
    <Panel
      header={
        <PanelHeader
          title={t("panel:title.assetDetails")}
          onClose={onClose as PanelHeaderProps["onClose"]}
          rightContent={
            <Stack direction="row" alignItems="center">
              <OpenInFullIcon sx={{ color: "primary.text", mr: 0.5 }} />
              <Link
                underline="hover"
                variant="button"
                fontWeight={500}
                color="primary.text"
                target="_blank"
                component={RouterLink}
                to={`${routePaths.asset.path}/${assetId}`}
              >
                {t("common:open")}
              </Link>
            </Stack>
          }
        />
      }
      open={open}
      onClose={onClose}
      reload={rowId}
      {...otherProps}
    >
      {asset === undefined || assetId !== asset.id ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={130} />
          <Stack direction="row" spacing={0.5}>
            <Skeleton variant="rounded" height={40} width={146} />
            <Skeleton variant="rounded" height={40} width={146} />
            <Skeleton variant="rounded" height={40} width={146} />
          </Stack>
          <Skeleton variant="rounded" height={15} />
          <Skeleton variant="rounded" height={30} />
          <Skeleton variant="rounded" height={15} width={220} />
          <Stack spacing={10}>
            <Box></Box>
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={15} />
              <Skeleton variant="rounded" height={15} width={292} />
              <Skeleton variant="rounded" height={15} width={146} />
            </Stack>
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={15} />
              <Skeleton variant="rounded" height={15} width={292} />
              <Skeleton variant="rounded" height={15} width={146} />
            </Stack>
            <Stack spacing={1}>
              <Skeleton variant="rounded" height={15} />
              <Skeleton variant="rounded" height={15} width={292} />
              <Skeleton variant="rounded" height={15} width={146} />
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <>
          <Stack>
            <Stack
              sx={{
                p: 3,
                ml: -3,
                mt: -3,
                mr: -3,
                background: `radial-gradient(59.89% 124.74% at 36.7% 64.33%,${AssetTypeAlphaColor(
                  asset.type,
                  0.1
                )} 0%, ${AssetTypeAlphaColor(asset.type, 0)} 100%), ${AssetTypeAlphaColor(asset.type, 0.05)}`,
              }}
              direction="row"
              alignItems="center"
              justifyContent="space-between"
            >
              <Box>
                <Typography variant="h6" color="text.tier" mb={0.5}>
                  {t("common:assetIdLowerCase")}
                </Typography>
                <Typography variant="h3" component="h2" mb={1.5}>
                  #{assetId}
                </Typography>
                <AssetStatus status={asset!.status} />
              </Box>
              <AssetThumbnail size="large" type={asset!.type} />
            </Stack>
            {(asset!.status === ObjectStatus.Rejected || asset!.status === ObjectStatus.RejectedAuto) && (
              <>
                <Stack spacing={1} mt={3}>
                  <MetadataListItem title={t("menu:title.rejectedBy")}>
                    <Link color="text.primary" underline="hover">
                      {asset?.rejectedBy}
                    </Link>
                  </MetadataListItem>
                  <MetadataListTextItem title={t("menu:title.rejectedAt")}>{asset?.rejectedAt}</MetadataListTextItem>
                </Stack>
                <Typography mt={2} color="error">
                  {asset?.rejectedComment}
                </Typography>
                <Typography mt={2} variant="h6" color="text.placeholder">
                  {t("panel:assetPanel.body.resolvingActions")}
                </Typography>
                <Grid container spacing={1} mt={1.5}>
                  <Grid item xs={6}>
                    <ModalRequestPurgeOpenButton
                      title={t("modal:title.confirmMediaPurgeAction")}
                      assetIds={[asset.id]}
                      startIcon={<RestartAltIcon />}
                      fullWidth
                      size="small"
                      disabled={!asset.userCanPurge}
                    >
                      {t("modal:body.purgeMediaFiles")}
                    </ModalRequestPurgeOpenButton>
                  </Grid>
                  <Grid item xs={6}>
                    <Button
                      fullWidth
                      size="small"
                      startIcon={<EditOutlinedIcon />}
                      onClick={handleEditMetadata}
                      disabled={!UserHasAcl(AclTypes.aclMetadataUpdate)}
                    >
                      {t("common:editMetadata")}
                    </Button>
                  </Grid>
                  <Grid item xs={6}>
                    <ModalConfirmActionOpenButton
                      displayComment
                      modalMessage={t("modal:title.confirmAssetDeletion")}
                      startIcon={<DeleteOutlinedIcon />}
                      onConfirm={(comment?: string) => handleDeleteAsset(comment)}
                      fullWidth
                      size="small"
                      color={"error"}
                      disabled={!UserHasAcls([AclTypes.aclIngestDeleteRequest, AclTypes.aclElevationWishlistCreate])}
                    >
                      {t("modal:body.deleteAsset")}
                    </ModalConfirmActionOpenButton>
                  </Grid>
                  <Grid item xs={6}>
                    <ModalConfirmActionOpenButton
                      displayComment
                      modalMessage={t("modal:title.confirmBypassRejection")}
                      startIcon={<AutorenewIcon />}
                      onConfirm={(comment?: string) => handleBypassRejection(comment)}
                      fullWidth
                      size="small"
                      color={"error"}
                      disabled={!asset.userCanBypassReject}
                    >
                      {t("modal:body.bypassRejection")}
                    </ModalConfirmActionOpenButton>
                  </Grid>
                  <Grid item xs={12}>
                    <ModalOrderFileFixingOpenButton fullWidth size="small" objectId={asset.id} />
                  </Grid>
                </Grid>
              </>
            )}
            {asset!.status === ObjectStatus.Deleted && (
              <>
                <Stack spacing={1} mt={3}>
                  <MetadataListItem title={t("menu:title.deletedBy")}>
                    <Link color="text.primary" underline="hover">
                      {asset?.deletedBy}
                    </Link>
                  </MetadataListItem>
                  <MetadataListTextItem title={t("menu:title.deletedAt")}>{asset?.deletedAt}</MetadataListTextItem>
                </Stack>
                <Typography mt={2} color="text.secondary">
                  {asset?.deletedComment}
                </Typography>
              </>
            )}
            {asset!.status === ObjectStatus.Accepted && asset!.isBypassed && (
              <>
                <Stack spacing={1} mt={3}>
                  <MetadataListItem title={t("menu:title.bypassedBy")}>
                    <Link color="text.primary" underline="hover">
                      {asset?.bypassedBy}
                    </Link>
                  </MetadataListItem>
                  <MetadataListTextItem title={t("menu:title.bypassedAt")}>{asset?.bypassedAt}</MetadataListTextItem>
                </Stack>
                <Typography mt={2} color="warning.main">
                  {asset?.bypassedComment}
                </Typography>
              </>
            )}
            <PanelTabs
              variant="fullWidth"
              value={tabValue}
              onChange={handleTabChange}
              sx={{
                //mt: asset!.status === ObjectStatus.Rejected || asset!.status === ObjectStatus.RejectedAuto ? 1 : 2,
                pr: 3,
                pl: 3,
              }}
            >
              <Tab label={t("common:metadata")} {...a11yProps(0)} />
              {asset.type === ObjectType.Folder ? (
                UserHasAcl(AclTypes.aclPermissionView) && <Tab label={t("common:permissions")} {...a11yProps(1)} />
              ) : (
                <Tab label={t("common:mediaFiles")} {...a11yProps(1)} />
              )}
              <Tab label={t("common:activities")} {...a11yProps(2)} />
            </PanelTabs>
            <Box pt={3}>
              <PanelTabContent index={0} activePanelIndex={tabValue} id="asset-details-panel">
                <AssetPanelMetadata asset={asset} />
              </PanelTabContent>
              <PanelTabContent index={1} activePanelIndex={tabValue} id="asset-details-panel">
                <AssetPanelMediaCards assetMedias={asset.assetMedia} assetType={asset.type} />
              </PanelTabContent>
              <PanelTabContent index={2} activePanelIndex={tabValue} id="asset-details-panel">
                <AssetPanelNotifications resourceId={asset.id} resourceKind={ActivityKind.Asset} />
              </PanelTabContent>
            </Box>
          </Stack>
        </>
      )}
    </Panel>
  );
};

export default AssetPanel;
// TODO : rework props like in asset-single => sections are interfaces that we pass to component (cleaner)
const AssetPanelMetadata = React.memo((props: { asset: AssetPanelDto }) => {
  const { t } = useTranslation();
  const { asset } = props;
  const [placeholderName, setPlaceholderName] = React.useState<string | null>(null);
  const [assetSize, setAssetSize] = React.useState<number | null>(null);

  const showAssetSize = React.useMemo(
    () => asset.globalInfos.classification === ObjectClass.Drive && asset.type === ObjectType.Folder,
    [asset]
  );

  useEffect(() => {
    if (asset.globalInfos.placeholderRowId && UserHasAcl(AclTypes.aclIngestPlaceholderCreate))
      ingestService.GetPlaceholderById(asset.globalInfos.placeholderRowId).then((response) => {
        if (response && response.placeholder) setPlaceholderName(response.placeholder.name);
      });

    if (showAssetSize) {
      fileService.GetFolderSize(asset.id).then((response) => {
        setAssetSize(response.size);
      });
    }
  }, [asset.id, showAssetSize]);

  return (
    <>
      <Stack spacing={3}>
        <Stack spacing={1}>
          {/*TODO : Maybe put link here ? as in current frontend*/}
          <Typography>{asset?.globalInfos.title}</Typography>
          <Typography color="text.tier">{asset?.globalInfos.assetName}</Typography>
        </Stack>
        <Stack direction="row" spacing={1.5}>
          <AssetTypeChip type={asset!.type} />
          <Chip label={asset?.globalInfos.classification} color="default" />
        </Stack>
        <Stack spacing={1}>
          <MetadataListTextItem title={t("menu:title.requestedBy")} color={"primary.text"}>
            {asset?.globalInfos.requestedBy}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.requestedAt")}>
            {asset?.globalInfos.requestedAt}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.providerAssigned")}>
            <Chip label={asset?.globalInfos.assignedProviderName} color="info" />
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.followedBy")}>
            {asset?.globalInfos.followedByUserNames}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.ingestDate")}>
            {asset?.globalInfos.ingestDate}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.uploadedVia")}>
            {asset?.globalInfos.uploadedVia !== "-" ? (
              <Chip label={asset?.globalInfos.uploadedVia} color="info" />
            ) : (
              asset?.globalInfos.uploadedVia
            )}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.acceptationDate")}>
            {asset?.globalInfos.acceptationDate}
          </MetadataListTextItem>
          <MetadataListTextItem title={t("menu:title.requestTemplate")}>{placeholderName ?? "-"}</MetadataListTextItem>
          {showAssetSize && (
            <MetadataListTextItem title={t("common:size")}>
              {assetSize === null ? <CircularProgress size={20} /> : GetFileSize(assetSize)}
            </MetadataListTextItem>
          )}
        </Stack>
      </Stack>
      <Divider sx={{ mt: 3, mb: 3, mr: -3, ml: -3 }} />
      <Typography variant="subtitle2" fontWeight={600}>
        {t("panel:assetPanel.body.assetMetadata")}
      </Typography>

      {asset?.assetMetadata.sections.map((section, index) => (
        <PanelAccordion key={index} title={section.name} defaultExpanded={false}>
          <Stack spacing={1}>
            {section.rows.map((row, subIndex) => (
              <MetadataListTextItem key={subIndex} title={row.label}>
                {assetService.FormatMetadataRow(row)}
              </MetadataListTextItem>
            ))}
          </Stack>
        </PanelAccordion>
      ))}
    </>
  );
});

const AssetPanelMediaCards = React.memo((props: { assetMedias: AssetMediaCardDto[]; assetType: ObjectType }) => (
  <Stack spacing={1}>
    {props.assetMedias.map((assetMedia, index) => {
      return (
        <MediaCard
          key={index}
          fileId={assetMedia.fileId}
          isDownloable={assetMedia.isDownloable}
          downloadLink={assetMedia.downloadLink}
          title={assetMedia.title}
          type={props.assetType}
          status={assetMedia.status}
          duration={assetMedia.duration}
          storageLocation={assetMedia.storageLocation}
          category={assetMedia.category}
          accessibility={assetMedia.accessibility}
          size={assetMedia.size}
          date={assetMedia.date}
        />
      );
    })}
  </Stack>
));

const AssetPanelNotifications = React.memo((props: { resourceId: number; resourceKind: ActivityKind }) => (
  <ResourceNotification resourceId={props.resourceId} resourceKind={props.resourceKind} />
));

const StyledTabs = styled(Tabs)(({ theme }) => ({
  position: "sticky",
  top: "65px",
  zIndex: 1,
  background: theme.palette.background.paper,
  marginLeft: theme.spacing(-3),
  marginRight: theme.spacing(-3),
}));

function a11yProps(index: number) {
  return {
    id: `asset-details-panel-tab-${index}`,
    "aria-controls": `asset-details-panel-tabpanel-${index}`,
  };
}
